/**
 * Current profile pictures keyed by stable person identity.
 *
 * Feed endpoints return `userPhoto` snapshots that can be older than the
 * `profilePicture` returned by the profile endpoint. Once a profile response
 * supplies the canonical value, every mounted avatar for that person should
 * use it instead of its stale endpoint-specific copy.
 */

export interface PersonAvatarIdentity {
  profileUserGuid?: string | null;
  userId?: string | number | null;
  username?: string | null;
}

const avatars = new Map<string, string>();
const listeners = new Set<() => void>();

const normalize = (value: string | number | null | undefined): string =>
  String(value ?? '').trim().toLowerCase();

const identityKeys = ({
  profileUserGuid,
  userId,
  username,
}: PersonAvatarIdentity): string[] => {
  const guid = normalize(profileUserGuid);
  const id = normalize(userId);
  const handle = normalize(username).replace(/^@+/, '');

  return [
    guid ? `guid:${guid}` : '',
    id ? `id:${id}` : '',
    handle ? `username:${handle}` : '',
  ].filter(Boolean);
};

export const getRegisteredPersonAvatar = (
  identity: PersonAvatarIdentity,
): string | undefined => {
  for (const key of identityKeys(identity)) {
    if (avatars.has(key)) return avatars.get(key);
  }
  return undefined;
};

export const registerPersonAvatar = (
  identity: PersonAvatarIdentity,
  avatarUrl: string | null | undefined,
): void => {
  const keys = identityKeys(identity);
  if (keys.length === 0) return;

  const normalizedUrl = avatarUrl?.trim() ?? '';
  let changed = false;
  keys.forEach(key => {
    if (avatars.get(key) === normalizedUrl) return;
    avatars.set(key, normalizedUrl);
    changed = true;
  });

  if (changed) listeners.forEach(listener => listener());
};

export const subscribeToPersonAvatars = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/** Test isolation for the module-level external store. */
export const clearPersonAvatarRegistry = (): void => {
  if (avatars.size === 0) return;
  avatars.clear();
  listeners.forEach(listener => listener());
};
